import memoize from 'transmute/memoize';
import indexBy from 'transmute/indexBy';
import get from 'immutable-less/transmute/get';
import { fromJS } from 'immutable';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import PropertyFieldTypes from 'automation-ui-flow-core/fields/PropertyFieldTypes';
import { getObjectTypePropertyLabel } from 'automation-ui-references/object-type-properties/getObjectTypePropertyLabel';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import CRMPropertyFieldTypes from 'automation-ui-flow-core/fields/CRMPropertyFieldTypes';
import CRMPropertyValueTypes from 'automation-ui-flow-core/fields/CRMPropertyValueTypes';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
// @ts-expect-error not typed
import { propertyDescriptionTranslator } from 'property-description-translator';
import { getFlpDisabledPropertyMessage } from 'automation-ui-core/utils/getFlpDisabledPropertyMessage';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Map of defaults from CRM property `type` to `fieldType`. This
 * type of mapping leads to some data loss as there are multiple
 * types of `fieldType`s per `type` of property, but it gives
 * us a starting point in the case that `fieldType` is not
 * defined (which is optional in the CRM schema world for
 * now).
 */
export const CrmPropertyDefinitionTypeToFieldTypeDefaults = {
  [CRMPropertyValueTypes.DATE]: PropertyFieldTypes.DATE,
  [CRMPropertyValueTypes.DATETIME]: PropertyFieldTypes.DATETIME,
  [CRMPropertyValueTypes.ENUM]: PropertyFieldTypes.ENUM,
  [CRMPropertyValueTypes.BOOL]: PropertyFieldTypes.BOOL,
  [CRMPropertyValueTypes.NUMBER]: PropertyFieldTypes.NUMBER,
  [CRMPropertyValueTypes.STRING]: PropertyFieldTypes.SINGLE_LINE_STRING,
  [CRMPropertyValueTypes.PHONE_NUMBER]: PropertyFieldTypes.PHONE_NUMBER
};
const MapCRMTypeToAddonType = Object.assign({}, CrmPropertyDefinitionTypeToFieldTypeDefaults, {
  [CRMPropertyFieldTypes.CHECKBOX]: PropertyFieldTypes.CHECKBOX,
  [CRMPropertyFieldTypes.BOOLEAN_CHECKBOX]: PropertyFieldTypes.BOOL,
  [CRMPropertyFieldTypes.RADIO]: PropertyFieldTypes.RADIO,
  [CRMPropertyFieldTypes.SELECT]: PropertyFieldTypes.ENUM,
  [CRMPropertyFieldTypes.TEXTAREA]: PropertyFieldTypes.MULTI_LINE_STRING,
  [CRMPropertyFieldTypes.HTML]: PropertyFieldTypes.HTML,
  [CRMPropertyFieldTypes.PHONE_NUMBER]: PropertyFieldTypes.PHONE_NUMBER,
  [CRMPropertyFieldTypes.CALCULATION_SCORE]: PropertyFieldTypes.CALCULATION_SCORE,
  [CRMPropertyFieldTypes.CALCULATION_ROLLUP]: PropertyFieldTypes.CALCULATION_ROLLUP,
  [CRMPropertyFieldTypes.CALCULATION_EQUATION]: PropertyFieldTypes.CALCULATION_EQUATION
});

/**
 * Flattens the CRM type + fieldType into a single type of addon input field type.
 * This is important because the CRM has cases where it mixes type vs fieldType.
 *
 * Most CRM properties look like this:
 *   fieldType: "enum" || "number" || "text" ...
 *   type: "string" || "number" ...
 * The **fieldType** lets us know what type of input method to render.
 *
 * However, one exception example are dates:
 * Ideally, dates should look like this:
 *   fieldType: "date",
 *   type: "text"
 * However, in reality actually looks like this:
 *   fieldType: "text"
 *   type: "date"
 *
 * This means that we need to look at the **type**, and not **fieldType** to know what type of
 * input method to render.
 *
 * The following method captures this logic, and returns a type meant for rendering a specific
 * input type.
 */
export const convertToAddonFieldType = (type, fieldType) => {
  if (fieldType === CRMPropertyFieldTypes.TEXT) {
    const addonType = MapCRMTypeToAddonType[type];
    if (addonType) {
      return addonType;
    }
  }
  const addonType = MapCRMTypeToAddonType[fieldType];
  if (addonType) {
    return addonType;
  }
  return PropertyFieldTypes.ENUM;
};
export const convertFieldTypeToPlatformValueType = addonFieldType => {
  if (addonFieldType === PropertyFieldTypes.PROPERTY) return PropertyValueTypes.OBJECT_PROPERTY;
  return PropertyValueTypes.STATIC_VALUE;
};
const convertEnumOptionToOption = option => {
  if ('options' in option) {
    const typedOption = option;
    const {
      label,
      options
    } = typedOption;
    return {
      text: label,
      value: '',
      options: options && options.map(convertEnumOptionToOption)
    };
  }
  if ('id' in option) {
    const typedOption = option;
    const {
      id,
      label,
      description
    } = typedOption;
    return {
      text: label,
      value: id || '',
      help: description
    };
  }
  const {
    label,
    value,
    description
  } = option;
  return {
    text: label,
    value: value || '',
    help: description
  };
};
export const formatEnumOptions = valueOptions => valueOptions.map(convertEnumOptionToOption);
export const isRestrictedSensitiveProperty = (property, dataSensitivityAllowlist) => {
  if (dataSensitivityAllowlist !== null && dataSensitivityAllowlist !== void 0 && dataSensitivityAllowlist.length && property.dataSensitivity) {
    return !dataSensitivityAllowlist.includes(property.dataSensitivity);
  }
  return false;
};
export const isFlpRestricted = property => {
  var _property$fieldLevelP;
  if (property.fieldLevelPermission === undefined) {
    console.warn(`Missing field level permissions on property ${property.name}`);
  }
  const accessLevel = (_property$fieldLevelP = property.fieldLevelPermission) === null || _property$fieldLevelP === void 0 ? void 0 : _property$fieldLevelP.accessLevel;
  return accessLevel !== 'not_specified' && accessLevel !== '';
};
export const getRestrictSensitivePropertyOverrides = (property, dataSensitivityAllowlist) => {
  const tooltip = I18n.text('automation-ui-action-features.object-type-properties.restricted-sensitive-property-tooltip');
  return isRestrictedSensitiveProperty(property, dataSensitivityAllowlist) ? {
    disabled: true,
    icon: 'hide',
    tooltip
  } : {};
};
export const getFlpOverrides = property => {
  const tooltip = getFlpDisabledPropertyMessage(property.name);
  return isFlpRestricted(property) ? {
    disabled: true,
    icon: 'hide',
    tooltip
  } : {};
};
export const getObjectTypePropertyDescriptionTranslation = ({
  description,
  hubspotDefined,
  name,
  objectTypeId
}) => {
  if (!hubspotDefined || !name || !objectTypeId || !description) {
    return description;
  }
  try {
    const translatedDescription = propertyDescriptionTranslator({
      description,
      name,
      objectTypeId
    });
    return translatedDescription || description;
  } catch (_unused) {
    return description;
  }
};

/**
 * NOTE(bheng): Existing use cases with Array.map() may be problematic since the
 *   second parameter is the array index. For example:
 *
 *   getFlattenedProperties(propertiesByGroup)
 *     .filter(_filterHiddenProperty)
 *     .map(convertToAddonPropertyOption);
 */
export const convertToAddonPropertyOption = (property, objectTypeId, enforceFlp, dataSensitivityAllowlist) => {
  const {
    label,
    name,
    type,
    fieldType,
    options,
    hubspotDefined,
    referencedObjectType,
    externalOptions,
    showCurrencySymbol,
    disabled,
    readOnlyValue,
    valueOptions,
    numberDisplayHint,
    description,
    hasMore
  } = property;
  const restrictSensitivePropertyOverrides = getRestrictSensitivePropertyOverrides(property, dataSensitivityAllowlist);
  const flpOverrides = enforceFlp ? getFlpOverrides(property) : {};
  return Object.assign({
    text: getObjectTypePropertyLabel(name, label, objectTypeId),
    value: name,
    fieldType: convertToAddonFieldType(type, fieldType),
    valueType: type,
    valueOptions: formatEnumOptions(valueOptions || options || []),
    hubspotDefined,
    hasMore,
    referencedObjectType,
    externalOptions,
    showCurrencySymbol,
    disabled,
    readOnlyValue,
    numberDisplayHint,
    help: getObjectTypePropertyDescriptionTranslation({
      description,
      hubspotDefined,
      name,
      objectTypeId
    }),
    objectTypeId,
    propertyDefinition: property
  }, restrictSensitivePropertyOverrides, flpOverrides);
};
export const getProperty = memoize((allGroupedProperties, propertyName) => {
  let propertyToFind;
  allGroupedProperties.some(group => {
    return group.properties.some(property => {
      if (property.name === propertyName) {
        propertyToFind = property;
        return true;
      }
      return false;
    });
  });
  return propertyToFind;
});
export const filterRequirementsRequiredProperties = propertyRequirements => propertyRequirements.reduce((acc, req) => {
  if (req.required) {
    acc.push(req.requirementIdentifier);
  }
  return acc;
}, []);
const flattenOptionValues = options => options.flatMap(option => option.options ? option.options.map(({
  value
}) => value) : option.value);
const flattenOptions = options => options.flatMap(option => option.options || option);
export const missingDestPropertyValueOptions = (destOptions, sourceOptions) => {
  // AddOnPropertyValueOption[], string, AddOnPropertyValueOption[]
  const destValues = indexBy(get('value'), fromJS(flattenOptions(destOptions)));
  const options = flattenOptions(sourceOptions);
  return options.filter(sourceOption => {
    return !destValues.has(sourceOption.value);
  });
};

/**
 * See https://knowledge.hubspot.com/articles/kcs_article/workflows/why-am-i-unable-to-select-my-target-property-when-copying-properties-using-a-workflow
 */
export const arePropertyTypesCopyCompatible = (destType, sourceType) => {
  if (!destType || !sourceType || destType === sourceType) {
    return true;
  }

  // Note: there is no entry for STRING because these cannot
  // be copied into any other property type. Also omitted are
  // source types that match destination types.
  switch (sourceType) {
    case CRMPropertyValueTypes.DATE:
      return destType === CRMPropertyValueTypes.DATETIME;
    case CRMPropertyValueTypes.DATETIME:
      return destType === CRMPropertyValueTypes.DATE;
    case CRMPropertyValueTypes.ENUM:
      {
        const enumPropertyValueTypesArr = [CRMPropertyValueTypes.STRING, CRMPropertyValueTypes.BOOL];
        return enumPropertyValueTypesArr.includes(destType);
      }
    case CRMPropertyValueTypes.NUMBER:
      return destType === CRMPropertyValueTypes.STRING;
    case CRMPropertyValueTypes.BOOL:
      return destType === CRMPropertyValueTypes.ENUM;
    case CRMPropertyValueTypes.PHONE_NUMBER:
      return destType === CRMPropertyValueTypes.STRING;
    default:
      return false;
  }
};
export const arePropertyValueOptionsCopyCompatible = (destOptions, sourceOptions) => {
  if (!destOptions || !sourceOptions) {
    return true;
  }
  const flattenedTargetValues = flattenOptionValues(destOptions);
  const flattenedSourceValues = flattenOptionValues(sourceOptions);
  return flattenedSourceValues.every(sourceValue => flattenedTargetValues.includes(sourceValue));
};
export const arePropertiesCopyCompatible = (destProperty, sourceProperty) => {
  if (!destProperty || !sourceProperty) {
    return true;
  }
  const destType = destProperty.valueType;
  const sourceType = sourceProperty.valueType;
  if (!arePropertyTypesCopyCompatible(destType, sourceType)) {
    return false;
  }
  if ((sourceType === CRMPropertyValueTypes.ENUM || sourceType === CRMPropertyValueTypes.BOOL) && (destType === CRMPropertyValueTypes.ENUM || destType === CRMPropertyValueTypes.BOOL)) {
    /**
     * NOTE(bheng): We can't fetch more than a single page of options for
     *   reference types so disable the compatibility check if we've reached
     *   the page max and there's more data. This only applies if we have
     *   incomplete information on the destination since we're making sure the
     *   source can be copied into the target.
     */
    const skipCheck = destProperty.referencedObjectType && destProperty.hasMore;
    if (skipCheck) return true;
    const destOptions = destProperty.valueOptions;
    const sourceOptions = sourceProperty.valueOptions;
    if (!arePropertyValueOptionsCopyCompatible(destOptions, sourceOptions)) {
      return false;
    }
  }
  return true;
};
export const getCopyCompatibleOptions = (targetProperties, sourceProperty, nonEmptyPropertyAssignments, onMissingOptionsTooltipClick, usageTracker, enforceFlp, dataSensitivityAllowlist) => {
  const nonEmptyTargetProperties = nonEmptyPropertyAssignments.map(nonEmptyPropertyAssignment => nonEmptyPropertyAssignment.targetProperty);
  return targetProperties.filter(targetProperty => arePropertyTypesCopyCompatible(targetProperty.valueType, sourceProperty.valueType)).map(targetProperty => {
    if (enforceFlp && targetProperty !== null && targetProperty !== void 0 && targetProperty.propertyDefinition && isFlpRestricted(targetProperty === null || targetProperty === void 0 ? void 0 : targetProperty.propertyDefinition)) {
      return Object.assign({}, targetProperty, getFlpOverrides(targetProperty.propertyDefinition));
    }
    if (dataSensitivityAllowlist !== null && dataSensitivityAllowlist !== void 0 && dataSensitivityAllowlist.length && targetProperty !== null && targetProperty !== void 0 && targetProperty.propertyDefinition && isRestrictedSensitiveProperty(targetProperty.propertyDefinition)) {
      return Object.assign({}, targetProperty, getRestrictSensitivePropertyOverrides(targetProperty.propertyDefinition, dataSensitivityAllowlist));
    }
    if (!arePropertiesCopyCompatible(targetProperty, sourceProperty)) {
      const missingOptions = missingDestPropertyValueOptions(targetProperty.valueOptions, sourceProperty.valueOptions);
      return Object.assign({}, targetProperty, {
        disabled: true,
        missingOptions,
        tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "automation-ui-action-features.object-type-properties.copy-incompatible.tooltip_jsx",
          elements: {
            Link: UIButton
          },
          options: {
            firstMissingOption: missingOptions[0].text,
            count: missingOptions.length,
            onClick: () => {
              onMissingOptionsTooltipClick(targetProperty, sourceProperty, missingOptions);
              trackEvent(usageTracker, Trackables.CLICK_MISMATCHED_VALUES_SUBPAGE);
            }
          }
        })
      });
    }
    if (nonEmptyTargetProperties.includes(targetProperty.value)) {
      return Object.assign({}, targetProperty, {
        disabled: true,
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-action-features.action-features.create-object.target-properties-select.non-empty-tooltip"
        })
      });
    }
    return Object.assign({}, targetProperty, {
      disabled: targetProperty.disabled || targetProperty.readOnlyValue,
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-action-features.action-features.create-object.target-properties-select.read-only-tooltip"
      })
    });
  });
};